// Public candidate intake form (no login) at /apply/:company (company = rsr|deroi).
// Mirrors the vendor-register pattern: validate the slug, collect basic info + CV,
// POST to /hr/recruitment/apply/:company which creates an hr_candidates row at
// stage='applied'. No auth header needed — the backend scopes by the URL slug.
import React, { useState, useEffect } from 'react';
import Logo from '../components/Logo';
import { useParams } from 'react-router-dom';
import api from '../services/api';

const SOURCES = [
  { v: 'naukri', label: 'Naukri' },
  { v: 'referral', label: 'Referral' },
  { v: 'walk_in', label: 'Walk-in' },
  { v: 'other', label: 'Other' },
];

export default function Apply() {
  const { company } = useParams();
  const [valid, setValid] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [form, setForm] = useState({ marital_status: '', reference_source: '' });
  const [cv, setCv] = useState(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get(`/hr/recruitment/apply/${company}`)
      .then(r => { setValid(true); setCompanyName(r.data?.data?.company_name || ''); })
      .catch(() => setValid(false));
  }, [company]);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v != null && v !== '') fd.append(k, v); });
      if (cv) fd.append('cv', cv);
      await api.post(`/hr/recruitment/apply/${company}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Submission failed');
    } finally { setSaving(false); }
  };

  const inputStyle = { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, boxSizing: 'border-box', marginTop: 4 };
  const labelStyle = { fontSize: 12, color: '#555', fontWeight: 600, display: 'block', marginTop: 14 };
  const sectionStyle = { fontSize: 12, fontWeight: 700, color: '#3B3A8F', textTransform: 'uppercase', letterSpacing: 1, margin: '20px 0 10px', paddingBottom: 6, borderBottom: '2px solid #EEEDF8' };

  if (valid === null) return <Centered>Validating link…</Centered>;
  if (valid === false) return (
    <Centered>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 48 }}>❌</div>
        <h2 style={{ color: '#E22D2D' }}>Invalid Link</h2>
        <p style={{ color: '#888' }}>This application link is not valid. Please check the URL.</p>
      </div>
    </Centered>
  );

  if (done) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontFamily: 'Arial', background: '#f0f2f8' }}>
      <div style={{ background: 'white', borderRadius: 12, padding: 40, textAlign: 'center', maxWidth: 480, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        <div style={{ fontSize: 56 }}>🎉</div>
        <h2 style={{ color: '#3B3A8F', margin: '16px 0 8px' }}>Application Received!</h2>
        <p style={{ color: '#666', fontSize: 14 }}>Thank you for applying{companyName ? ` to ${companyName}` : ''}. Our HR team will review your details and reach out if shortlisted.</p>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: 'transparent', fontFamily: 'inherit', padding: '30px 16px' }}>
      <div style={{ maxWidth: 600, margin: '0 auto' }}>
        <div style={{ background: 'white', borderRadius: 12, padding: '20px 28px', marginBottom: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 16 }}>
          <Logo height={30} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#222' }}>Job Application{companyName ? ` — ${companyName}` : ''}</div>
            <div style={{ fontSize: 12, color: '#888' }}>Fill in your details to apply</div>
          </div>
        </div>

        {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '10px 16px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div style={{ background: 'white', borderRadius: 12, padding: 28, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <div style={sectionStyle}>Your Details</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={labelStyle}>Full Name *</label>
                <input style={inputStyle} required placeholder="Your full name" onChange={e => f('candidate_name', e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Contact Number *</label>
                <input style={inputStyle} required type="tel" placeholder="+91 XXXXX XXXXX" onChange={e => f('contact_no', e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Email ID</label>
                <input style={inputStyle} type="email" placeholder="your@email.com" onChange={e => f('email_id', e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Post Applied For *</label>
                <input style={inputStyle} required placeholder="e.g. Tailor, Merchandiser" onChange={e => f('designation_applied', e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Marital Status</label>
                <select style={inputStyle} value={form.marital_status} onChange={e => f('marital_status', e.target.value)}>
                  <option value="">—</option>
                  <option value="single">Single</option>
                  <option value="married">Married</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>How did you hear about us?</label>
                <select style={inputStyle} value={form.reference_source} onChange={e => f('reference_source', e.target.value)}>
                  <option value="">—</option>
                  {SOURCES.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
                </select>
              </div>
              {(form.reference_source === 'referral' || form.reference_source === 'other') && (
                <div>
                  <label style={labelStyle}>{form.reference_source === 'referral' ? 'Referred By' : 'Please specify'}</label>
                  <input style={inputStyle} onChange={e => f('reference_detail', e.target.value)} />
                </div>
              )}
            </div>

            <div style={sectionStyle}>Resume / CV</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>📎 Upload CV (PDF / DOC / DOCX)</label>
                <input style={{ ...inputStyle, padding: '8px' }} type="file" accept=".pdf,.doc,.docx" onChange={e => setCv(e.target.files[0])} />
              </div>
              <div>
                <label style={labelStyle}>…or paste a CV link</label>
                <input style={inputStyle} placeholder="https://drive.google.com/…" onChange={e => f('cv_link', e.target.value)} />
              </div>
            </div>

            <button type="submit" disabled={saving} style={{ marginTop: 24, width: '100%', padding: '14px', background: '#3B3A8F', color: 'white', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
              {saving ? 'Submitting…' : '✅ Submit Application'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const Centered = ({ children }) => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontFamily: 'Arial' }}>{children}</div>
);
